import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Table, VisibilityState } from '@tanstack/react-table';
import { Button } from 'common/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from 'common/ui/sheet';
import { Settings2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SortableItem } from './sort-able-item';
interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  tableName?: string;
}

export function DataTableViewOptions<TData>({
  table,
  tableName,
}: DataTableViewOptionsProps<TData>) {
  const [isOpen, setIsOpen] = useState(false);

  // Generate unique keys for saving column order and visibility based on the page path
  const columnOrderKey = `columnOrder:${tableName}`;
  const columnVisibilityKey = `columnVisibility:${tableName}`;

  // Initialize column order from localStorage or default to the table's column order
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem(columnOrderKey);
    const currentColumnIds = table.getAllColumns().map(col => col.id);

    if (savedOrder) {
      const parsedOrder = JSON.parse(savedOrder);
      // Check if saved order contains all current columns and no extra columns
      const isValidOrder =
        Array.isArray(parsedOrder) &&
        parsedOrder.every(id => currentColumnIds.includes(id)) &&
        currentColumnIds.every(id => parsedOrder.includes(id));

      if (isValidOrder) {
        return parsedOrder;
      } else {
        // Clear invalid order from localStorage
        localStorage.removeItem(columnOrderKey);
      }
    }
    return currentColumnIds;
  });

  // Initialize column visibility from localStorage
  const [columnVisibility, setColumnVisibility] =
    useState <
    VisibilityState >
    (() => {
      const savedVisibility = localStorage.getItem(columnVisibilityKey);
      const currentColumnIds = table.getAllColumns().map(col => col.id);

      if (savedVisibility) {
        const parsedVisibility = JSON.parse(savedVisibility);
        // Check if saved visibility only contains current columns
        const isValidVisibility =
          typeof parsedVisibility === 'object' &&
          Object.keys(parsedVisibility).every(key =>
            currentColumnIds.includes(key)
          );

        if (isValidVisibility) {
          return parsedVisibility;
        } else {
          // Clear invalid visibility from localStorage
          localStorage.removeItem(columnVisibilityKey);
        }
      }
      return {};
    });

  // Save column order to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(columnOrderKey, JSON.stringify(columnOrder));
  }, [columnOrder, columnOrderKey]);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(columnVisibilityKey, JSON.stringify(columnVisibility));
  }, [columnVisibility, columnVisibilityKey]);

  // Sync column order with the table instance
  useEffect(() => {
    table.setColumnOrder(columnOrder);
  }, [columnOrder, table]);

  // Sync column visibility with the table instance
  useEffect(() => {
    table.setColumnVisibility(columnVisibility);
  }, [columnVisibility, table]);

  // Handle visibility change
  const handleVisibilityChange = (columnId: string, isVisible: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: isVisible,
    }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setColumnOrder(prev => {
        if (
          Array.isArray(prev) &&
          prev.every(item => typeof item === 'string')
        ) {
          const oldIndex = prev.indexOf(active.id);
          const newIndex = prev.indexOf(over.id);
          return arrayMove(prev, oldIndex, newIndex);
        } else {
          console.error('Invalid column order:', prev);
          return prev;
        }
      });
    }
  };

  const handleResetAll = () => {
    if (window.confirm('Are you sure you want to reset all column settings?')) {
      // Clear localStorage keys
      localStorage.removeItem(columnOrderKey);
      localStorage.removeItem(columnVisibilityKey);

      // Reset column order and visibility
      const defaultOrder = table.getAllColumns().map(col => col.id);
      setColumnOrder(defaultOrder);
      setColumnVisibility({});
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="ml-auto hidden h-8 lg:flex"
        >
          <Settings2 />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="max-w-2xl mx-auto p-6 h-full shadow-lg bg-transparent backdrop-blur-sm"
      >
        <SheetHeader>
          <SheetTitle className="text-lg font-semibold text-center text-white">
            Manage Columns
          </SheetTitle>
        </SheetHeader>
        <div className="h-[calc(100vh-99px)] overflow-y-auto px-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columnOrder}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3 mt-2">
                {columnOrder.map(columnId => {
                  const column = table.getColumn(columnId);
                  return (
                    <SortableItem
                      key={column.id}
                      id={column.id}
                      column={column}
                      onVisibilityToggle={value =>
                        handleVisibilityChange(column.id, value)
                      }
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          <div className="mt-6">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleResetAll}
              className="w-full"
            >
              Reset All
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
